/**
 * Turn-taking for the voice interview.
 *
 * We do not time the silence. Deepgram already reports end-of-speech; the open
 * question is whether the speaker was *finished* or merely thinking. A pause
 * mid-thought and a pause at the end of a thought are the same silence, so a
 * stopwatch cannot tell them apart — but the words can.
 */
// Words that almost never end a spoken sentence. If the transcript trails off on
// one of these, the speaker was still going — a stopwatch can't know this.
const TRAILING_INCOMPLETE_WORDS = new Set([
  // conjunctions
  'and', 'but', 'so', 'or', 'nor', 'yet', 'because', 'since', 'although', 'though',
  'while', 'whereas', 'unless', 'until', 'if', 'when', 'whenever', 'as',
  // relative / complementizer
  'that', 'which', 'who', 'whom', 'whose', 'where', 'why', 'how',
  // prepositions
  'of', 'to', 'in', 'on', 'at', 'for', 'with', 'from', 'by', 'about', 'into',
  'over', 'under', 'through', 'after', 'before', 'between', 'against', 'during',
  // articles / determiners / possessives
  'a', 'an', 'the', 'my', 'your', 'his', 'her', 'its', 'our', 'their', 'this',
  'these', 'those', 'some', 'any', 'every', 'each', 'both',
  // auxiliaries and copulas
  'is', 'was', 'were', 'am', 'are', 'be', 'been', 'being', 'had', 'has', 'have',
  'do', 'does', 'did', 'will', 'would', 'can', 'could', 'should', 'shall',
  'may', 'might', 'must', 'got', 'gonna', 'wanna',
  // fillers
  'um', 'uh', 'er', 'ah', 'like', 'well', 'okay', 'so-', 'mmm', 'hmm',
  // subject pronouns — "and my mother, she…" / "and then we…". Only checked when
  // there is no terminal punctuation, so "I did it." is unaffected.
  'i', 'he', 'she', 'we', 'they', 'it', 'you', 'there',
]);

// Words that cannot end a sentence even when smart_format has put a full stop
// after them. Used to override punctuation, so keep this list tight.
const NEVER_ENDS_SENTENCE = new Set([
  'and', 'but', 'or', 'nor', 'so', 'because', 'since', 'although', 'though',
  'while', 'unless', 'until', 'whereas',
  'of', 'to', 'in', 'on', 'at', 'for', 'with', 'from', 'by', 'into', 'onto',
  'a', 'an', 'the', 'my', 'your', 'his', 'her', 'its', 'our', 'their',
  'um', 'uh',
]);

/**
 * Decide whether the speaker actually finished, from what they said rather than
 * from how long they've been quiet.
 *
 * Returns 'unfinished' | 'uncertain' | 'finished'.
 *
 * Deliberately conservative: only a positive signal (a trailing connective, or a
 * comma/dash) counts as 'unfinished'. Missing punctuation alone is only
 * 'uncertain', so that if Deepgram's smart_format stops punctuating we degrade to
 * a short-ish wait rather than making every single turn wait the full pause.
 */
export const classifyTurnEnd = (text) => {
  const cleaned = String(text || '').trim();
  if (!cleaned) return 'uncertain';

  // Explicit "still going" punctuation.
  if (/[,\-–—:;]$/.test(cleaned) || /\.\.\.$/.test(cleaned)) return 'unfinished';

  const lastWord = (cleaned
    .toLowerCase()
    .replace(/["')\]]+$/, '')
    .replace(/[.!?]+$/, '')
    .match(/[a-z'-]+$/) || [''])[0];

  // Punctuation wins, so "That's it." and "I told you." read as finished.
  if (/[.!?]["')\]]?$/.test(cleaned)) {
    // ...unless it fell on a word no sentence can end on, which means
    // smart_format guessed wrong mid-thought.
    if (lastWord && NEVER_ENDS_SENTENCE.has(lastWord)) return 'unfinished';
    // Give a beat to a one-or-two word reply — "Brooklyn." is usually the start
    // of an answer, not the whole of it.
    return cleaned.split(/\s+/).filter(Boolean).length < 4 ? 'uncertain' : 'finished';
  }

  // No terminal mark. Now a trailing connective or pronoun is a strong signal
  // that they were still going: "...and my mother, she" / "...because".
  if (lastWord && TRAILING_INCOMPLETE_WORDS.has(lastWord)) return 'unfinished';

  return 'uncertain';
};
